'use client'

import { useCallback, useEffect, useState } from 'react'
import Cookies from 'js-cookie'
import { userInfoApi } from '../configs'
import { get } from '@/service/base'

export type IProject = {
  id: string
  name: string
  admin_capable: boolean
  domain?: string
  domain_id?: string
  roles?: [
    {
      id: string
      name: string
    },
  ]
  system_capable?: boolean
  system_policies?: string[]
  domain_capable?: boolean
  domain_policies?: string[]
}

export type IUserInfo = {
  admin_policies: string[]
  allow_web_console: boolean
  created_at: string
  domain: { id: string; name: string }
  enable_mfa: false
  enable_quota_check: boolean
  enabled: boolean
  failed_auth_count: number
  id: string
  is_local: boolean
  is_system_account: boolean
  last_active_at: string
  last_login_ip: string
  last_login_source: string
  name: string
  need_reset_password: false
  non_default_domain_projects: false
  projectDomain: string
  projectDomainId: string
  projectId: string
  projectName: string
  projects: IProject[]
  regions: string[]
  roles: string[]
  services: { status: boolean; type: string }[]
  system_policies: string[]
  system_totp_on: boolean
}

export type UserInfoFetcher = () => Promise<IUserInfo | null>

export function useUserInfo(
  baseUrl?: string,
): [IUserInfo | null, UserInfoFetcher] {
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(null)
  const fetchUserInfo = useCallback(async () => {
    const u = await get(`${baseUrl ?? ''}${userInfoApi}`, {}, { prefix: false })
    try {
      setUserInfo(u.data ?? null)
      return u.data ?? null
    }
    catch (e) {
      console.error('failed to get user info', e)
      setUserInfo(null)
      return null
    }
  }, [])

  useEffect(() => {
    fetchUserInfo()
  }, [fetchUserInfo])

  return [userInfo, fetchUserInfo]
}

export type ProjectListFetcher = () => Promise<IProject[] | null>

export function useProjectList(
  baseUrl?: string,
): [IProject[] | null, ProjectListFetcher] {
  const [projectList, setProjectList] = useState<IProject[] | null>(null)
  const [, fetchUserInfo] = useUserInfo(baseUrl)

  const fetchProjectList = useCallback(async () => {
    const userInfo = await fetchUserInfo()
    const res = userInfo?.projects ?? null
    setProjectList(res)
    return res
  }, [fetchUserInfo])

  useEffect(() => {
    fetchProjectList()
  }, [fetchProjectList])

  return [projectList, fetchProjectList]
}

export function useCurrentProject() {
  const [currentProject, setCurrentProject] = useState<IProject | null>(null)
  const [projectList] = useProjectList()
  const [userInfo] = useUserInfo()
  const [isAdmin, setIsAdmin] = useState(false)
  const [isDomain, setIsDomain] = useState(false)
  const [isSystemAdmin, setIsSystemAdmin] = useState(false)
  const [isDomainAdmin, setIsDomainAdmin] = useState(false)
  const [isAdminMode, setIsAdminMode] = useState(false)
  const [isDomainMode, setIsDomainMode] = useState(false)

  const scope = Cookies.get('scope') as 'project' | 'domain' | 'system' | undefined
  useEffect(() => {
    setCurrentProject(
      projectList?.find(i => i.id === userInfo?.projectId) ?? null,
    )
    setIsAdmin(scope === 'system')
    setIsDomain(scope === 'domain')
    setIsSystemAdmin(!!projectList?.some(item => item.id === userInfo?.projectId && item.system_capable))
    setIsDomainAdmin(!!projectList?.some(item => item.id === userInfo?.projectId && item.domain_capable))
    setIsAdminMode(isAdmin && isSystemAdmin)
    setIsDomainMode(isDomain && isDomainAdmin)
  }, [projectList, userInfo?.projectId, isAdmin, isSystemAdmin, isAdminMode, scope, isDomain, isDomainAdmin])

  return { currentProject, isAdmin, isSystemAdmin, isAdminMode, isDomainMode, isDomain, isDomainAdmin, scope, userInfo }
}
